/**
 * @file Phone Auth View.
 */

import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { withRouter, matchPath } from 'react-router-dom';
import qs from 'query-string';
import get from 'lodash.get';
import compose from 'lodash.flowright';

import MainLayout from '../layouts/MainLayout';
import ContentWrap from '../../components/global/ContentWrap';
import ContentHeader from '../../components/global/ContentHeader';
import PhoneAuthWithEmailFallback from '../../components/auth/PhoneAuthWithEmailFallback';
import ConsumerEmailAuthAllowed from '../../components/auth/ConsumerEmailAuthAllowed';
import withGoogleRecaptchaProvider from '../../components/auth/hocs/withGoogleRecaptchaProvider';

const propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }),
};

const getIdFromNextPath = next => {
  const paths = [
    '/discover/album-folder/:albumFolderId',
    '/purchase/album-folder/:albumFolderId',
    '/purchase/album/:albumId',
    '/galleries/view/folder/:folderId',
  ];

  const match = paths.reduce((acc, path) => {
    const pathMatch = matchPath(next, {
      path,
      exact: false,
    });

    return pathMatch || acc;
  }, {});

  return (
    get(match, 'params.albumFolderId', '') ||
    get(match, 'params.albumId', '') ||
    get(match, 'params.folderId')
  );
};

const PhoneAuthView = ({ location, history }) => {
  const { next } = qs.parse(location.search);
  const id = getIdFromNextPath(next);

  return (
    <MainLayout>
      <Helmet>
        <title>Welcome to Waldo Photos &ndash; Waldo</title>
      </Helmet>

      <ContentWrap size="sm">
        <ContentHeader title="Welcome to Waldo Photos!" />

        <ConsumerEmailAuthAllowed id={id}>
          {allowed => (
            <PhoneAuthWithEmailFallback
              onAuthSuccess={() => {
                if (
                  (location?.state?.originalUrl ?? '').includes(
                    '/s/all-photos-access/',
                  )
                ) {
                  history.replace(location.state.originalUrl);
                } else if (next) {
                  history.replace(next);
                } else {
                  history.replace('/app');
                }
              }}
              bypassEmailVerification
              emailVerificationRedirectUrl={next}
              enableEmail={allowed}
              next={next}
              introText="Please verify your mobile number to access this gallery. We’ll send you a verification code."
              submitText="Send SMS"
            />
          )}
        </ConsumerEmailAuthAllowed>
      </ContentWrap>
    </MainLayout>
  );
};

PhoneAuthView.propTypes = propTypes;

export default compose(withRouter, withGoogleRecaptchaProvider)(PhoneAuthView);
